import { fetchUtils } from "react-admin";
import {
  AuthenticationResult,
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import {
  TokenProvider,
  getAppContextHeaders,
} from "../../app/app.context.provider";

/**
 * Generates an HTTP client function that uses Azure AD tokens for authentication.
 *
 * @param adClient - An instance of the PublicClientApplication for Azure AD.
 * @returns A function that makes HTTP requests with Azure AD authentication headers.
 */
export const httpClient = (adClient: PublicClientApplication) => {
  return async (url: string, options: fetchUtils.Options = {}) => {
    try {
      const account = adClient.getAllAccounts()[0];

      if (!account) {
        // Don't trigger login here - let the auth provider handle it
        console.error("[MSAL] No accounts found. Authentication required.");
        throw new Error("No accounts found. Please log in.");
      }

      let tokenResponse: AuthenticationResult;
      
      try {
        // Try silent token acquisition first
        tokenResponse = await adClient.acquireTokenSilent({
          scopes: ["openid", "profile", "User.Read", "offline_access"],
          account: account,
        });
      } catch (silentError) {
        console.warn("[MSAL] Silent token acquisition failed, attempting interactive login:", silentError);
        
        // If silent acquisition fails due to interaction required, fall back to popup
        if (silentError instanceof InteractionRequiredAuthError) {
          try {
            tokenResponse = await adClient.acquireTokenPopup({
              scopes: ["openid", "profile", "User.Read", "offline_access"],
              account: account,
            });
            console.log("[MSAL] Interactive token acquisition successful");
          } catch (interactiveError) {
            console.error("[MSAL] Interactive token acquisition failed:", interactiveError);
            throw interactiveError;
          }
        } else {
          throw silentError;
        }
      }

      if (!tokenResponse || !tokenResponse.idToken) {
        throw new Error("Failed to acquire token.");
      }

      const requestHeaders = getAzureAdHeaders(url, tokenResponse.idToken, options);

      // Make the HTTP request with the acquired token
      return fetchUtils.fetchJson(url, {
        ...options,
        headers: requestHeaders,
      });
    } catch (error) {
      console.error("Error in httpClient:", error);
      throw error;
    }
  };
};

export const getAzureAdHeaders = (
  url: string,
  token: string | null,
  options: fetchUtils.Options | undefined
): Headers => {
  const headers = ((options && options.headers) ||
    new Headers({
      Accept: "application/json",
    })) as Headers;
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
 
  getAppContextHeaders(url, headers);
  return headers;
};

export const getTokenProvider = (
  adClient: PublicClientApplication
): TokenProvider => {
  return {
    getIdToken: async () => {
      try {
        const account = adClient.getAllAccounts()[0];

        if (!account) {
          // Don't trigger login here - let the auth provider handle it
          console.error("[MSAL] No accounts found in getIdToken. Authentication required.");
          throw new Error("No accounts found. Please log in.");
        }

        let tokenResponse: AuthenticationResult;
        
        try {
          // Try silent token acquisition first (includes automatic refresh)
          tokenResponse = await adClient.acquireTokenSilent({
            scopes: ["openid", "profile", "User.Read", "offline_access"],
            account: account,
          });
          console.log("[MSAL] Silent token acquisition successful");
        } catch (silentError) {
          console.warn("[MSAL] Silent token acquisition failed, attempting interactive login:", silentError);
          
          // If silent acquisition fails due to interaction required, fall back to popup
          if (silentError instanceof InteractionRequiredAuthError) {
            try {
              tokenResponse = await adClient.acquireTokenPopup({
                scopes: ["openid", "profile", "User.Read", "offline_access"],
                account: account,
              });
              console.log("[MSAL] Interactive token acquisition successful");
            } catch (interactiveError) {
              console.error("[MSAL] Interactive token acquisition failed:", interactiveError);
              throw interactiveError;
            }
          } else {
            throw silentError;
          }
        }

        if (!tokenResponse || !tokenResponse.idToken) {
          throw new Error("Failed to acquire token.");
        }

        return tokenResponse.idToken;
      } catch (error) {
        console.error("[MSAL] Error in getIdToken:", error);
        throw error;
      }
    },
  };
};
